/**
 * Product Settings - types and configuration constants.
 *
 * Both the SKU tokens and the label fields are declared as plain arrays here so
 * that adding a new one (MRP, expiry date, HUID, ...) is a data change only:
 * neither the settings screen nor the label builder needs to be touched.
 */

export type LabelFieldKey =
    | "productName"
    | "variantName"
    | "sellingPrice"
    | "brandName"
    | "categoryName"
    | "weightUnit";

export type SkuSeparator = "-" | "_" | "";

/** Sheet layout the label PDF is rendered on. */
export type LabelLayoutKey = "A4_3" | "A4_2" | "THERMAL" | "CUSTOM";

export interface ProductSkuSettings {
    enabled: boolean;
    prefix: string;
    separator: SkuSeparator;
    includeCategory: boolean;
    includeBrand: boolean;
    includeVariant: boolean;
    sequenceLength: number;
    startNumber: number;
}

export interface ProductLabelSettings {
    /** Ordered list of selected fields - the order is the print order. */
    fields: LabelFieldKey[];
    /** Sheet layout used when the labels are rendered to PDF. */
    layout: LabelLayoutKey;
    /** Geometry for the Custom layout. Kept even while a preset is selected. */
    custom: CustomLabelLayout;
}

export interface ProductSettings {
    version: number;
    sku: ProductSkuSettings;
    label: ProductLabelSettings;
}

/**
 * Loose product shape accepted by the SKU builder and the label builder.
 * Kept structural so it can be fed from the product list row, the add/edit
 * product formik values or a variant row without any mapping.
 */
export interface ProductLike {
    productName?: string | null;
    variantName?: string | null;
    sellingPrice?: string | number | null;
    brandName?: string | null;
    categoryName?: string | null;
    weight?: string | number | null;
    weightUnit?: string | null;
}

export interface LabelFieldOption {
    key: LabelFieldKey;
    /** Label shown to the user in settings. */
    label: string;
    /** Sample value used by the label preview card. */
    sample: string;
    /** Extracts the printable value from a product. */
    getValue: (product: ProductLike) => string;
}

export interface SkuTokenOption {
    /** Settings flag that switches this token on. */
    key: Extract<keyof ProductSkuSettings, "includeCategory" | "includeBrand" | "includeVariant">;
    label: string;
    helpText: string;
    /** Source value on the product, e.g. "Cosmetics" -> "COS". */
    getSource: (product: ProductLike) => string;
    /** Value used for the live preview when no real product is available. */
    sample: string;
}

export interface SeparatorOption {
    label: string;
    value: SkuSeparator;
}

// Schema version. Bump when the stored shape changes in a non-additive way.
export const PRODUCT_SETTINGS_VERSION = 1;

/**
 * Key prefixes. The concrete keys are built lazily per store in
 * productSettingsStorage, because config.storeId is only known after sign in.
 */
export const PRODUCT_SETTINGS_STORAGE_KEY = "@zencore/product_settings";
export const SKU_COUNTER_STORAGE_KEY = "@zencore/sku_counter";

// Input constraints, shared by the screen validation and the storage sanitiser.
export const SKU_PREFIX_MAX_LENGTH = 5;
export const SKU_SEQUENCE_MIN_LENGTH = 3;
export const SKU_SEQUENCE_MAX_LENGTH = 8;
export const SKU_START_NUMBER_MIN = 1;
export const SKU_START_NUMBER_MAX = 999999999;
/** Number of characters taken from category / brand / variant names. */
export const SKU_TOKEN_LENGTH = 3;

export const SEPARATOR_OPTIONS: SeparatorOption[] = [
    { label: "Hyphen ( - )", value: "-" },
    { label: "Underscore ( _ )", value: "_" },
    { label: "None", value: "" },
];

export const SKU_TOKEN_OPTIONS: SkuTokenOption[] = [
    {
        key: "includeCategory",
        label: "Category code",
        helpText: "First 3 letters of the category name",
        getSource: (product) => product?.categoryName ?? "",
        sample: "Cosmetics",
    },
    {
        key: "includeBrand",
        label: "Brand code",
        helpText: "First 3 letters of the brand name",
        getSource: (product) => product?.brandName ?? "",
        sample: "Loreal",
    },
    {
        key: "includeVariant",
        label: "Variant code",
        helpText: "First 3 letters of the variant name",
        getSource: (product) => product?.variantName ?? "",
        sample: "Ruby",
    },
];

export const LABEL_FIELD_OPTIONS: LabelFieldOption[] = [
    {
        key: "productName",
        label: "Product Name",
        sample: "Matte Lipstick",
        getValue: (product) => String(product?.productName ?? ""),
    },
    {
        key: "variantName",
        label: "Variant Name",
        sample: "Ruby Red",
        getValue: (product) => String(product?.variantName ?? ""),
    },
    {
        key: "sellingPrice",
        label: "Price",
        sample: "499.00",
        getValue: (product) =>
            product?.sellingPrice === null || product?.sellingPrice === undefined
                ? ""
                : String(product.sellingPrice),
    },
    {
        key: "brandName",
        label: "Brand Name",
        sample: "L'Oreal",
        getValue: (product) => String(product?.brandName ?? ""),
    },
    {
        key: "categoryName",
        label: "Category Name",
        sample: "Cosmetics",
        getValue: (product) => String(product?.categoryName ?? ""),
    },
    {
        key: "weightUnit",
        label: "Weight / Unit",
        sample: "3.5 g",
        getValue: (product) =>
            [product?.weight, product?.weightUnit].filter((part) => part !== null && part !== undefined && part !== "").join(" "),
    },
];

export const LABEL_FIELD_KEYS: LabelFieldKey[] = LABEL_FIELD_OPTIONS.map((option) => option.key);

export interface LabelLayoutOption {
    key: LabelLayoutKey;
    label: string;
    description: string;
    /** Page size in millimetres. */
    pageWidthMm: number;
    pageHeightMm: number;
    pageMarginMm: number;
    /** Label grid. THERMAL is a single label per page. */
    columns: number;
    labelWidthMm: number;
    labelHeightMm: number;
    gapMm: number;
    /** Cut guides only make sense on a sheet, not on a continuous roll. */
    showCutGuides: boolean;
    /**
     * Inner geometry. The presets leave these out and let the renderer derive
     * them from the label size; the Custom layout sets them explicitly.
     */
    barcodeWidthMm?: number;
    barcodeHeightMm?: number;
    contentWidthMm?: number;
    contentHeightMm?: number;
    contentGapMm?: number;
}

/**
 * User defined geometry behind the Custom layout, all in millimetres.
 * Every value is also a key in CUSTOM_LAYOUT_FIELDS, which drives both the
 * settings form and the validation, so a new dimension is a data change only.
 */
export interface CustomLabelLayout {
    pageWidthMm: number;
    pageHeightMm: number;
    pageMarginMm: number;
    columns: number;
    labelWidthMm: number;
    labelHeightMm: number;
    gapMm: number;
    barcodeWidthMm: number;
    barcodeHeightMm: number;
    contentWidthMm: number;
    contentHeightMm: number;
    contentGapMm: number;
    showCutGuides: boolean;
}

export type CustomLayoutNumericKey = Exclude<keyof CustomLabelLayout, "showCutGuides">;

export interface CustomLayoutField {
    key: CustomLayoutNumericKey;
    label: string;
    helpText: string;
    /** Section heading the field is grouped under in the settings form. */
    group: "Page" | "Label" | "Barcode" | "Content";
    min: number;
    max: number;
    /** Columns is a count; everything else is a millimetre measurement. */
    unit: "mm" | "count";
    /** Allows fractional millimetres such as 1.5 mm gaps. */
    allowDecimal: boolean;
}

export const CUSTOM_LAYOUT_FIELDS: CustomLayoutField[] = [
    {
        key: "pageWidthMm",
        label: "Page width",
        helpText: "Total paper width",
        group: "Page",
        min: 20,
        max: 1000,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "pageHeightMm",
        label: "Page height",
        helpText: "Total paper height",
        group: "Page",
        min: 20,
        max: 1000,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "pageMarginMm",
        label: "Page margin",
        helpText: "Unprintable edge around the sheet",
        group: "Page",
        min: 0,
        max: 50,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "columns",
        label: "Labels per row",
        helpText: "How many labels sit side by side",
        group: "Page",
        min: 1,
        max: 12,
        unit: "count",
        allowDecimal: false,
    },
    {
        key: "labelWidthMm",
        label: "Label width",
        helpText: "Width of a single label",
        group: "Label",
        min: 10,
        max: 500,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "labelHeightMm",
        label: "Label height",
        helpText: "Height of a single label",
        group: "Label",
        min: 10,
        max: 500,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "gapMm",
        label: "Gap between labels",
        helpText: "Spacing between labels on the sheet",
        group: "Label",
        min: 0,
        max: 50,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "barcodeWidthMm",
        label: "Barcode width",
        helpText: "Width of the barcode block",
        group: "Barcode",
        min: 5,
        max: 500,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "barcodeHeightMm",
        label: "Barcode height",
        helpText: "Height of the bars, excluding the code text",
        group: "Barcode",
        min: 3,
        max: 200,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "contentWidthMm",
        label: "Content width",
        helpText: "Width of each printed field line",
        group: "Content",
        min: 5,
        max: 500,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "contentHeightMm",
        label: "Content height",
        helpText: "Height of each field line; the text scales to fit",
        group: "Content",
        min: 1,
        max: 100,
        unit: "mm",
        allowDecimal: true,
    },
    {
        key: "contentGapMm",
        label: "Space between contents",
        helpText: "Vertical gap between the field lines",
        group: "Content",
        min: 0,
        max: 50,
        unit: "mm",
        allowDecimal: true,
    },
];

export const CUSTOM_LAYOUT_GROUPS: CustomLayoutField["group"][] = ["Page", "Label", "Barcode", "Content"];

/** Starts from the A4 3-per-row preset, so Custom opens on something printable. */
export const DEFAULT_CUSTOM_LABEL_LAYOUT: CustomLabelLayout = {
    pageWidthMm: 210,
    pageHeightMm: 297,
    pageMarginMm: 7,
    columns: 3,
    labelWidthMm: 65,
    labelHeightMm: 35,
    gapMm: 0,
    barcodeWidthMm: 55,
    barcodeHeightMm: 12,
    contentWidthMm: 60,
    contentHeightMm: 4,
    contentGapMm: 1,
    showCutGuides: true,
};

/**
 * Adding a layout here is enough: the settings screen lists it and the PDF
 * generator derives the page size and grid from these numbers.
 */
export const LABEL_LAYOUT_OPTIONS: LabelLayoutOption[] = [
    {
        key: "A4_3",
        label: "A4 sheet - 3 per row",
        description: "24 labels per page, 65 x 35 mm",
        pageWidthMm: 210,
        pageHeightMm: 297,
        pageMarginMm: 7,
        columns: 3,
        labelWidthMm: 65,
        labelHeightMm: 35,
        gapMm: 0,
        showCutGuides: true,
    },
    {
        key: "A4_2",
        label: "A4 sheet - 2 per row",
        description: "12 labels per page, 95 x 40 mm",
        pageWidthMm: 210,
        pageHeightMm: 297,
        pageMarginMm: 8,
        columns: 2,
        labelWidthMm: 95,
        labelHeightMm: 40,
        gapMm: 4,
        showCutGuides: true,
    },
    {
        key: "THERMAL",
        label: "Thermal roll - one per label",
        description: "Single 50 x 25 mm label per page",
        pageWidthMm: 50,
        pageHeightMm: 25,
        pageMarginMm: 1.5,
        columns: 1,
        labelWidthMm: 47,
        labelHeightMm: 22,
        gapMm: 0,
        showCutGuides: false,
    },
    {
        key: "CUSTOM",
        label: "Custom",
        description: "Set the page, label, barcode and content sizes yourself",
        ...DEFAULT_CUSTOM_LABEL_LAYOUT,
    },
];

export const LABEL_LAYOUT_KEYS: LabelLayoutKey[] = LABEL_LAYOUT_OPTIONS.map((option) => option.key);

export const getLabelLayout = (key: LabelLayoutKey): LabelLayoutOption =>
    LABEL_LAYOUT_OPTIONS.find((option) => option.key === key) ?? LABEL_LAYOUT_OPTIONS[0];

/**
 * The layout actually used for rendering. Presets are returned as declared;
 * CUSTOM is the preset entry with the user's saved dimensions applied on top.
 */
export const resolveLabelLayout = (label: ProductLabelSettings): LabelLayoutOption => {
    const option = getLabelLayout(label.layout);
    if (option.key !== "CUSTOM") {
        return option;
    }
    return { ...option, ...(label.custom ?? DEFAULT_CUSTOM_LABEL_LAYOUT) };
};

export const DEFAULT_PRODUCT_SETTINGS: ProductSettings = {
    version: PRODUCT_SETTINGS_VERSION,
    sku: {
        enabled: true,
        prefix: "",
        separator: "-",
        includeCategory: true,
        includeBrand: false,
        includeVariant: false,
        sequenceLength: 5,
        startNumber: 1,
    },
    label: {
        fields: ["productName", "variantName", "sellingPrice"],
        layout: "A4_3",
        custom: DEFAULT_CUSTOM_LABEL_LAYOUT,
    },
};

/** Sample product backing the SKU and label previews. */
export const SAMPLE_PREVIEW_PRODUCT: ProductLike = {
    productName: "Matte Lipstick",
    variantName: "Ruby",
    sellingPrice: "499.00",
    brandName: "Loreal",
    categoryName: "Cosmetics",
    weight: "3.5",
    weightUnit: "g",
};

/** Always printed on the label, never user selectable. */
export const ALWAYS_PRINTED_NOTE =
    "The barcode and its human readable code are always printed and cannot be removed.";

export interface LabelDataItem {
    key: LabelFieldKey;
    label: string;
    value: string;
}
